export interface Waiting {
  kind: "waiting";
}

export interface Loading {
  kind: "loading";
}

export interface Ready {
  kind: "ready";
  pricing: PricingPayload;
}

export const waiting = (): Waiting => {
  return {
    kind: "waiting",
  };
};

export const loading = (): Loading => {
  return {
    kind: "loading",
  };
};

export const ready = (pricing: PricingPayload): Ready => {
  return {
    kind: "ready",
    pricing,
  };
};

export type PricingState = Waiting | Loading | Ready;

export interface BaseCoursePricing {
  currency_symbol: string;
  amount: string;
  currency_code: string;
  course_price_tooltip_text: string;
  gift_url: string;
  checkout_url: string;
  currency_name: string;
}

export interface SingleCoursePricing extends BaseCoursePricing {
  header: string;
  description: string;
  payment_plan_amount?: string;
  payment_plan_total_amount?: string;
  payment_plan_installment_count?: number;
  pay_in_full_checkout_url?: string;
  // Paying the course in one go against the three instalments. Zero and
  // absent when the course has no payment plan.
  upfront_saving_percent?: number;
  upfront_saving?: string | null;
}

export interface MonthlyCoursePricing extends BaseCoursePricing {
  monthly_plan_present: boolean;
  monthly_amount: string;
}

export interface YearlyCoursePricing extends BaseCoursePricing {
  full_plan_quotation_present: boolean;
  full_plan_present: boolean;
  full_plan: {
    currency_symbol: string;
    amount: string;
    currency_name: string;
    currency_code: string;
  };
  monthly_plan_present: boolean;
  monthly_amount: string;
  calculated_monthly_amount: string;
  // What twelve instalments add up to -- the full annual price, not the
  // discounted upfront one in `amount`.
  monthly_total: string;
  // Whole-number percent saved by paying upfront instead. Zero when there is
  // no saving to claim.
  upfront_saving_percent: number;
  // The same saving in money, absent when there is none.
  upfront_saving?: string | null;
  // The saving as whole monthly payments ("2 months free"). Absent unless it
  // comes to a whole number of months.
  upfront_months_free?: number | null;
  // The Pass paid in one go, as opposed to checkout_url which spreads it.
  pay_in_full_checkout_url: string;
  installment_count: number;
}

interface NoPricing {
  kind: "no-pricing";
}

export interface Pricing {
  kind: "pricing";
  fee: number;
  existing_subscription: string;
  existing_subscription_present: boolean;
  enrolment_present: boolean;
  user_country: string;
  user_country_name: string;
  course_count: number;
  enrolment_count: number;
  courses_value: string;
  is_restricted_pricing_disabled: boolean;
  single_course_pricing: SingleCoursePricing;
  monthly_pricing: MonthlyCoursePricing;
  yearly_pricing: YearlyCoursePricing;
  lifetime_pricing: YearlyCoursePricing;
  community_pricing: YearlyCoursePricing;
}

export type PricingPayload = NoPricing | Pricing;
